import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { marked } from 'marked';
import DOMPurify from 'dompurify';

/**
 * Markdown Widget - Text/notes widget for dashboard documentation
 * Renders basic markdown (headers, bold, italic, links, code, lists)
 * Content is stored in widget.description field (no backend schema changes)
 */
const MarkdownWidget = ({ data, config = {} }) => {
  // Markdown source is the widget's own content field only. This used to fall
  // back to data.query / data.promqlQuery, which meant a NON-markdown widget's
  // SQL could be handed to the HTML renderer below.
  const content = data?.description || '';

  const renderedHtml = useMemo(() => {
    if (!content) return '';
    // Rendered with marked, then sanitised with DOMPurify (both already project
    // dependencies) rather than hand-rolled regexes.
    //
    // The previous renderer escaped &, < and > but NOT quotes, then interpolated
    // the raw link target into an attribute:
    //     [x](" onmouseover="alert(1))  ->  <a href="" onmouseover="alert(1)" ...>
    // which is script execution inside dangerouslySetInnerHTML, from content any
    // dashboard editor can author and every viewer of that dashboard renders.
    // javascript: targets were unfiltered too. Sanitising the final HTML closes
    // the whole class rather than patching the two escapes that were missed.
    const raw = marked.parse(content, { breaks: true, gfm: true });
    return DOMPurify.sanitize(raw, {
      ALLOWED_TAGS: [
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'br', 'hr',
        'strong', 'em', 'del', 'code', 'pre', 'blockquote',
        'ul', 'ol', 'li', 'a', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'span',
      ],
      ALLOWED_ATTR: ['href', 'title', 'target', 'rel', 'align'],
      // Anchors only — no javascript:/data: targets.
      ALLOWED_URI_REGEXP: /^(?:https?:|mailto:|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
      ADD_ATTR: ['target'],
    });
  }, [content]);

  if (!content) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          p: 2,
        }}
      >
        <Typography color="text.secondary" variant="body2">
          No content. Edit this widget to add markdown text.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100%',
        overflow: 'auto',
        p: 2,
        color: 'text.primary',
        fontSize: '0.875rem',
        '& h1, & h2, & h3, & h4, & h5, & h6': {
          color: 'text.primary',
          fontWeight: 600,
        },
        '& a': {
          color: 'primary.main',
        },
        '& code': {
          fontFamily: 'monospace',
        },
        '& pre': {
          fontFamily: 'monospace',
        },
      }}
      dangerouslySetInnerHTML={{ __html: renderedHtml }}
    />
  );
};

export default React.memo(MarkdownWidget, (prevProps, nextProps) => {
  return (
    prevProps.data?.description === nextProps.data?.description
  );
});
